import {makeAutoObservable, reaction} from "mobx";
import {DialogStoreInterface} from "../DialogStore";
import {SlsPrinterItem, slsPrinterStore} from "./SlsPrinterStore";
import {api, catchApi} from "../../api";
import {PlacementProcessOriginDto} from "../../api/sls";
import {userPrinterPreferenceStore} from "../ums";

interface CartItem {
    id: string
    customerId: string
    correlationId: string
    origin?: PlacementProcessOriginDto
}

class SlsPlacementCartStore implements DialogStoreInterface {
    get cabinetRequired(): boolean {
        return this._cabinetRequired;
    }

    set cabinetRequired(value: boolean) {
        this._cabinetRequired = value;
    }
    get items(): CartItem[] {
        return this._items;
    }

    private set items(value: CartItem[]) {
        this._items = value;
    }
    get cabinet(): string | undefined {
        return this._cabinet;
    }

    set cabinet(value: string | undefined) {
        this._cabinet = value;
    }
    get printer(): SlsPrinterItem | undefined {
        return this._printer;
    }

    set printer(value: SlsPrinterItem | undefined) {
        this._printer = value;
    }
    get customerId(): string | undefined {
        return this._customerId;
    }

    private set customerId(value: string | undefined) {
        this._customerId = value;
    }

    private get itemMap(): Map<string, CartItem> {
        return this._itemMap;
    }

    get executing(): boolean {
        return this._executing
    }

    set executing(value: boolean) {
        this._executing = value
    }

    get dialogErrorMessage(): string | undefined {
        return this._dialogErrorMessage
    }

    set dialogErrorMessage(value: string | undefined) {
        this._dialogErrorMessage = value
    }

    get dialogOpen(): boolean {
        return this._dialogOpen
    }

    set dialogOpen(value: boolean) {
        this._dialogOpen = value
    }

    get ready(): boolean {
        const cabinetOk = this.cabinetRequired ? Boolean(this.cabinet) : true
        return cabinetOk && Boolean(this.printer?.id) && Boolean(!this.isEmpty())
    }

    private _dialogOpen: boolean = false
    private _dialogErrorMessage?: string | undefined
    private _executing: boolean = false

    private _customerId: string | undefined
    private _itemMap: Map<string, CartItem> = new Map()
    private _items: CartItem[] = []
    private _printer: SlsPrinterItem | undefined
    private _cabinet: string | undefined
    private _cabinetRequired: boolean = true

    constructor() {
        makeAutoObservable(this)
        reaction(() => [
            userPrinterPreferenceStore.zebraPrinterId,
            slsPrinterStore.options
        ], () => this.resetDefaultPrinter())
    }

    resetDefaultPrinter() {
        const printerId = userPrinterPreferenceStore.zebraPrinterId
        const options = slsPrinterStore.options
        this.printer = options.find(p => p.id === printerId) || undefined
    }

    async push(item: CartItem): Promise<boolean> {
        if (this.customerId && item.customerId !== this.customerId) {
            return false
        }
        if (this.exists(item.id)) {
            return false
        }
        this.customerId = item.customerId

        const response = await catchApi(async () => await api.sls.api.isCompletable(item.id))

        if (response?.data.isCompletable) {
            this.itemMap.set(item.id, item)
            this.items.push(item)
            return true
        } else {
            if (this.isEmpty()) {
                this.customerId = undefined
            }
            return false
        }
    }

    remove(item: CartItem) {
        if (!this.exists(item.id)) {
            return
        }
        const index = this.indexOf(item)

        if (index > -1) {
            this.items.splice(index, 1)
            this.itemMap.delete(item.id)
        }
        if (this.items.length === 0) {
            this.customerId = undefined
        }
    }

    indexOf(item: CartItem) {
        return this.items.findIndex(e => e.id === item.id)
    }

    addIsPossible(item: CartItem): boolean {
        if (item.origin === PlacementProcessOriginDto.STS) {
            return false
        }
        if (this.exists(item.id)) {
            return false
        }
        if (!this.customerId) {
            return true
        }
        return this.customerId === item.customerId
    }

    exists(id: string): boolean {
        return this.itemMap.has(id)
    }

    isEmpty(): boolean {
        return this.items.length === 0
    }

    clear() {
        this.customerId = undefined
        this.itemMap.clear()
        this.items = []
    }

    sortByDrag(dropIndex: number, draggingIndex: number) {
        const newItems = [...this.items];
        const [draggedItem] = newItems.splice(draggingIndex, 1);
        newItems.splice(dropIndex, 0, draggedItem);
        this.items = newItems
    }

    async submit() {
        this.executing = true
        await catchApi(async () => await api.sls.api.batchCompletePlacementProcess({
            cabinet: this.cabinet!,
            ids: this.items.map(e => e.id),
            printerId: this.printer?.id!
        }))
        this.clear()
        this.executing = false
        this.dialogOpen = false
    }
}

const slsPlacementCartStore = new SlsPlacementCartStore()

export {slsPlacementCartStore}
